import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Shield, Copy, Check, ArrowLeft, Smartphone } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from '../../hooks/use-toast';

const MFASetup = ({ onComplete, onBack }) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  // Use temporary token for MFA setup (session-based storage)
  const token = sessionStorage.getItem('temp_token') || sessionStorage.getItem('token');
  
  // Get user email from token if user object is not available
  const getUserEmail = () => {
    if (user?.email) return user.email;
    
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.email || payload.sub; // Try email first, then subject
      } catch (e) {
        console.error('Error decoding token for email:', e);
      }
    }
    return 'Unknown User';
  };
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('setup'); // 'setup', 'scan', 'verify'
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedBackup, setCopiedBackup] = useState(false);
  const { backendUrl } = useAuth();

  useEffect(() => {
    if (token && step === 'scan' && !setupData) {
      setupMFA();
    }
  }, [token, step, setupData]);

  const setupMFA = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get user ID from token or use the user object
      let userId = user?.id;
      
      // If we don't have user object, try to decode token to get user_id
      if (!userId && token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userId = payload.user_id;
        } catch (e) {
          console.error('Error decoding token:', e);
        }
      }
      
      if (!userId) {
        setError('User information not available. Please try logging in again.');
        return;
      }

      const response = await fetch(`${backendUrl}/api/mfa/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({}) // Empty body, let backend get user from token
      });

      if (response.ok) {
        const data = await response.json();
        setSetupData(data);
        setStep('scan');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to setup MFA');
      }
    } catch (err) {
      console.error('MFA Setup Error:', err);
      setError('Network error occurred while setting up MFA');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyMFA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get user ID from token or use the user object
      let userId = user?.id;
      
      // If we don't have user object, try to decode token to get user_id
      if (!userId && token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userId = payload.user_id;
        } catch (e) {
          console.error('Error decoding token:', e);
        }
      }
      
      if (!userId) {
        setError('User information not available. Please try logging in again.');
        return;
      }

      const response = await fetch(`${backendUrl}/api/mfa/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          totp_code: verificationCode
        })
      });

      if (response.ok) {
        toast({
          title: "MFA Enabled Successfully",
          description: "Two-factor authentication has been enabled for your account.",
          variant: "success"
        });
        onComplete();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Invalid verification code');
      }
    } catch (err) {
      console.error('MFA Verification Error:', err);
      setError('Network error occurred during verification');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      if (type === 'secret') {
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 2000);
      } else {
        setCopiedBackup(true);
        setTimeout(() => setCopiedBackup(false), 2000);
      }
      toast({
        title: "Copied to clipboard",
        description: `${type === 'secret' ? 'Secret key' : 'Backup codes'} copied successfully`,
        variant: "success"
      });
    });
  };

  const copyAllBackupCodes = () => {
    if (setupData?.backup_codes) {
      const codesText = setupData.backup_codes.join('\n');
      copyToClipboard(codesText, 'backup');
    }
  };

  if (step === 'setup') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
            <Shield className="h-8 w-8 text-blue-600" />
          </div>
          <CardTitle>Setup Two-Factor Authentication</CardTitle>
          <p className="text-sm text-muted-foreground">
            Enhance your account security with Google Authenticator
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-muted-foreground">Setting up MFA...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Why enable MFA?</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Protects against unauthorized access</li>
                  <li>• Required for enhanced security features</li>
                  <li>• Easy to use with mobile authenticator apps</li>
                </ul>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={onBack}
                  className="flex-1 px-4 py-2 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
                  style={{ 
                    backgroundColor: theme.buttonSecondaryColor,
                    borderColor: theme.borderColor,
                    color: theme.textColor
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = theme.hoverBackgroundColor}
                  onMouseLeave={(e) => e.target.style.backgroundColor = theme.buttonSecondaryColor}
                >
                  <ArrowLeft className="h-4 w-4 mr-2 inline" />
                  Back
                </button>
                <button
                  onClick={() => {
                    setStep('scan');
                    // Setup will be called by useEffect when step changes
                  }}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                  style={{ 
                    backgroundColor: isLoading ? '#9CA3AF' : theme.buttonColor
                  }}
                  onMouseEnter={(e) => !isLoading && (e.target.style.backgroundColor = theme.buttonHoverColor)}
                  onMouseLeave={(e) => !isLoading && (e.target.style.backgroundColor = theme.buttonColor)}
                >
                  {isLoading ? 'Setting up...' : 'Continue'}
                </button>
              </div>
            </div>
          )}
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  if (step === 'scan') {
    if (isLoading) {
      return (
        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
              <Smartphone className="h-8 w-8 text-blue-600" />
            </div>
            <CardTitle>Setting up MFA...</CardTitle>
            <p className="text-sm text-muted-foreground">
              Generating your authentication keys
            </p>
          </CardHeader>
          <CardContent className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-muted-foreground">Please wait...</p>
          </CardContent>
        </Card>
      );
    }

    if (error) {
      return (
        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="text-center">
            <CardTitle>Setup Error</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setError(null);
                  setStep('setup');
                }}
                className="flex-1 px-4 py-2 border text-sm font-medium rounded-md transition-colors"
                style={{ 
                  backgroundColor: theme.buttonSecondaryColor,
                  borderColor: theme.borderColor,
                  color: theme.textColor
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = theme.hoverBackgroundColor}
                onMouseLeave={(e) => e.target.style.backgroundColor = theme.buttonSecondaryColor}
              >
                <ArrowLeft className="h-4 w-4 mr-2 inline" />
                Back
              </button>
              <button
                onClick={() => {
                  setError(null);
                  setupMFA();
                }}
                className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors"
                style={{ backgroundColor: theme.buttonColor }}
                onMouseEnter={(e) => e.target.style.backgroundColor = theme.buttonHoverColor}
                onMouseLeave={(e) => e.target.style.backgroundColor = theme.buttonColor}
              >
                Retry
              </button>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!setupData) {
      return (
        <Card className="w-full max-w-md mx-auto">
          <CardContent className="text-center py-8">
            <p className="text-gray-600">Preparing MFA setup...</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <Smartphone className="h-8 w-8 text-green-600" />
          </div>
          <CardTitle>Scan QR Code</CardTitle>
          <p className="text-sm text-muted-foreground">
            Use Google Authenticator or any compatible TOTP app
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* QR Code Section */}
            <div className="space-y-4">
              <h3 className="font-medium">1. Scan with your authenticator app</h3>
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200 flex justify-center">
                {setupData.qr_code_url ? (
                  <img 
                    src={setupData.qr_code_url} 
                    alt="MFA QR Code" 
                    className="w-48 h-48"
                  />
                ) : (
                  <div className="w-48 h-48 flex items-center justify-center bg-gray-100 rounded">
                    <p className="text-sm text-gray-500">Loading QR code...</p>
                  </div>
                )}
              </div>
            </div>

            {/* Manual Setup Section */}
            <div className="space-y-4">
              <h3 className="font-medium">2. Or enter manually</h3>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-600 mb-2">Secret Key:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm bg-white p-2 rounded border font-mono break-all">
                    {setupData.secret_key}
                  </code>
                  <button
                    onClick={() => copyToClipboard(setupData.secret_key, 'secret')}
                    className="p-2 text-gray-500 hover:text-gray-700"
                    title="Copy secret key"
                  >
                    {copiedSecret ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs font-medium text-amber-800 mb-2">App Settings:</p>
                <ul className="text-xs text-amber-700 space-y-1">
                  <li>• Account: {getUserEmail()}</li>
                  <li>• Issuer: Payment Gateway Dashboard</li>
                  <li>• Type: Time-based (TOTP)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Backup Codes Section */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-red-900">3. Save your backup codes</h3>
              <button
                onClick={copyAllBackupCodes}
                className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
              >
                {copiedBackup ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                Copy All
              </button>
            </div>
            <p className="text-sm text-red-800 mb-3">
              Store these backup codes in a safe place. Each code can only be used once.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {setupData.backup_codes?.map((code, index) => (
                <code key={index} className="bg-white p-2 rounded text-sm font-mono text-center">
                  {code}
                </code>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setStep('setup')}
              className="px-4 py-2 border text-sm font-medium rounded-md transition-colors"
              style={{ 
                backgroundColor: theme.buttonSecondaryColor,
                borderColor: theme.borderColor,
                color: theme.textColor
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = theme.hoverBackgroundColor}
              onMouseLeave={(e) => e.target.style.backgroundColor = theme.buttonSecondaryColor}
            >
              <ArrowLeft className="h-4 w-4 mr-2 inline" />
              Back
            </button>
            <button
              onClick={() => setStep('verify')}
              className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors"
              style={{ backgroundColor: theme.buttonColor }}
              onMouseEnter={(e) => e.target.style.backgroundColor = theme.buttonHoverColor}
              onMouseLeave={(e) => e.target.style.backgroundColor = theme.buttonColor}
            >
              Continue to Verification
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  if (step === 'verify') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <Shield className="h-8 w-8 text-green-600" />
          </div>
          <CardTitle>Verify Setup</CardTitle>
          <p className="text-sm text-muted-foreground">
            Enter the 6-digit code from your authenticator app
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="verification-code" className="block text-sm font-medium text-gray-700">
              Verification Code
            </label>
            <input
              type="text"
              id="verification-code"
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setVerificationCode(value);
                if (error) setError(null);
              }}
              maxLength={6}
              placeholder="000000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-center text-2xl font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoComplete="off"
            />
            <p className="text-xs text-gray-500">
              The code refreshes every 30 seconds
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setStep('scan')}
              className="px-4 py-2 border text-sm font-medium rounded-md transition-colors"
              style={{ 
                backgroundColor: theme.buttonSecondaryColor,
                borderColor: theme.borderColor,
                color: theme.textColor
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = theme.hoverBackgroundColor}
              onMouseLeave={(e) => e.target.style.backgroundColor = theme.buttonSecondaryColor}
            >
              <ArrowLeft className="h-4 w-4 mr-2 inline" />
              Back
            </button>
            <button
              onClick={verifyMFA}
              disabled={isLoading || verificationCode.length !== 6}
              className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              style={{ 
                backgroundColor: (isLoading || verificationCode.length !== 6) ? '#9CA3AF' : theme.successColor
              }}
              onMouseEnter={(e) => !(isLoading || verificationCode.length !== 6) && (e.target.style.backgroundColor = theme.successColor)}
              onMouseLeave={(e) => !(isLoading || verificationCode.length !== 6) && (e.target.style.backgroundColor = theme.successColor)}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                  Verifying...
                </>
              ) : (
                'Complete Setup'
              )}
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default MFASetup;